"use client";

import type { ComponentProps } from "react";
import {
  BookOpen,
  Calculator,
  GalleryVerticalEnd,
  LucideLayoutDashboard,
  ShoppingCart,
  ShoppingBag,
  Package,
  LucideHousePlus,
  Sprout,
  Trees,
  Users,
  Boxes,
  Warehouse,
  UserCog,
  Settings,
  Truck,
} from "lucide-react";

import { NavMain } from "@/components/nav-main";
import { NavUser } from "@/components/nav-user";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from "@/components/ui/sidebar";
import { useAuthStore } from "@/features/auth/stores/authStore";
import type { LucideIcon } from "lucide-react";

type NavSubItem = {
  title: string;
  url: string;
  permission?: string;
  kind?: "label";
};
type NavItem = {
  title: string;
  url: string;
  icon?: LucideIcon;
  isActive?: boolean;
  permission?: string;
  items?: NavSubItem[];
};

const navMainConfig: NavItem[] = [
  {
    title: "Dashboard",
    url: "/dashboard",
    icon: LucideLayoutDashboard,
    isActive: false,
    permission: "dashboard.view",
  },
  {
    title: "Orders",
    url: "#",
    icon: ShoppingBag,
    isActive: false,
    items: [
      { title: "Orders", url: "/production/orders", permission: "orders.view" },
      { title: "Sales Review Queue", url: "/production/sales-review", permission: "orders.confirm_with_allocation" },
      { title: "Forecast Requests", url: "/production/forecast-requests", permission: "forecast_requests.acknowledge" },
      { title: "Field Oversight", url: "/production/field-oversight", permission: "field_oversight.view" },
      { title: "Order Amendment Queue", url: "/production/order-amendment-approvals", permission: "orders.amend_approve" },
      { title: "Order Amendment History", url: "/production/order-amendment-history", permission: "orders.amend_view" },
      { title: "Walk-in Sale", url: "/production/walk-in-sale", permission: "orders.walkin_sale" },
      { title: "Payments", url: "/payments", permission: "payments.view" },
      { title: "Wallet Approvals", url: "/finance/wallet-approvals", permission: "wallets.approve" },
      { title: "Points Approvals", url: "/finance/points-approvals", permission: "loyalty.approve" },
    ],
  },
  {
    title: "Sales",
    url: "#",
    icon: ShoppingCart,
    items: [
      { title: "Customers", url: "/customers", permission: "customers.view" },
      { title: "Sales Summary", url: "/sales/summary", permission: "sales.view_summary" },
      { title: "Discount Approvals", url: "/sales/discount-approvals", permission: "discounts.view_approvals" },
      { title: "Invoicing", url: "/invoicing", permission: "invoices.view" },
      { title: "Void Requests", url: "/invoicing/void-requests", permission: "invoices.approve_void" },
      { title: "Credit Notes", url: "/invoicing/credit-notes", permission: "invoices.view_credit_notes" },
      { title: "Call Center", url: "/call-center", permission: "call_center.access" },
    ],
  },
  {
    title: "Inventory",
    url: "#",
    icon: Package,
    items: [
      { title: "Suppliers", url: "/inventory/suppliers", permission: "inventory.view" },
      { title: "Receive Stock", url: "/inventory/stock-recepit", permission: "inventory.create" },
      { title: "Batch Alerts", url: "/inventory/batches/alerts", permission: "inventory.view" },
      { title: "Store Inventory", url: "/inventory/store", permission: "inventory.view" },
      { title: "View Requisitions", url: "/inventory/requisitions", permission: "requisitions.view" },
      { title: "Create Requisition", url: "/inventory/requisition", permission: "requisitions.create" },
      { title: "Requisition Approvals", url: "/inventory/requisitions", permission: "requisitions.approve" },
      { title: "Requisition Issue Queue", url: "/inventory/requisitions", permission: "requisitions.issue" },
      { title: "My Returns", url: "/inventory/my-returns", permission: "requisitions.return" },
      { title: "Return Approvals", url: "/inventory/return-approvals", permission: "requisitions.approve_return" },
      { title: "Input Discard Approvals", url: "/inventory/discard-approvals", permission: "discard_requests.approve" },
      { title: "Stock Receipt Approvals", url: "/inventory/stock-receipt-approvals", permission: "inventory.approve_stock_receipt" },
      { title: "Supplier Approvals", url: "/inventory/supplier-approvals", permission: "inventory.approve_supplier" },
    ],
  },
  {
    title: "Production",
    url: "#",
    icon: LucideHousePlus,
    items: [
      { title: "Instructions", url: "#", kind: "label" },
      { title: "Manage Sowing Instructions", url: "/production/manage-instructions", permission: "sowing_instructions.view" },
      { title: "Create Sowing Instruction", url: "/production/create-sowing-instruction", permission: "sowing_instructions.create" },
      { title: "Bulk Import Stock", url: "/production/bulk-import-stock", permission: "sowing_instructions.import" },
      { title: "Pending Acceptance", url: "/production/pending-acceptance", permission: "sowing_instructions.view" },
      { title: "Sowing Amendment Approvals", url: "/production/amendment-approvals", permission: "sowing_instructions.approve" },
      { title: "Sowing Amendment History", url: "/production/amendment-history", permission: "sowing_instructions.view" },
      { title: "Ready for Growing", url: "/production/ready-for-growing", permission: "sowing_instructions.view" },

      { title: "Operations", url: "#", kind: "label" },
      { title: "Sowing Planner", url: "/production/sowing-planner", permission: "sowing_instructions.view" },
      { title: "Record Sowing", url: "/production/record-sowing", permission: "sowing_instructions.record_sowing" },
      { title: "Record Germination", url: "/production/record-germination", permission: "sowing_instructions.record_germination" },
      { title: "Record Growth", url: "/production/record-growth", permission: "production_activities.create" },
      { title: "Ready for Dispatch", url: "/production/record-dispatch", permission: "orders.dispatch" },
      { title: "Material Usage", url: "/production/material-usage", permission: "sowing_instructions.record_material_usage" },
      { title: "Payment Threshold Overrides", url: "/production/payment-threshold-overrides", permission: "orders.view_payment_threshold_override" },

      { title: "Stock & Quality", url: "#", kind: "label" },
      { title: "Expo Management", url: "/expos", permission: "expos.view" },
      { title: "Greenhouse Stock", url: "/greenhouse-stock", permission: "greenhouse_stock.view" },
      { title: "Greenhouse Layout", url: "/production/greenhouse-layout", permission: "greenhouse_stock.view" },
      { title: "Greenhouse Stock-Take", url: "/production/greenhouse-stock-take", permission: "greenhouse_stock.view" },
      { title: "Reserved Stock Orders", url: "/production/reserved-orders", permission: "orders.view" },
      { title: "Mark Overgrowns", url: "/production/overgrown-discard", permission: "discard_requests.create" },
      { title: "Discard Approvals", url: "/production/discard-approvals", permission: "discard_requests.approve" },
      { title: "Reserve Release Approvals", url: "/production/reserve-release-approvals", permission: "greenhouse_stock.approve_reserve_release" },
      { title: "Greenhouse Transfers", url: "/production/greenhouse-transfers", permission: "greenhouse_transfers.view" },
      { title: "Greenhouse Transfer Approvals", url: "/production/greenhouse-transfer-approvals", permission: "greenhouse_transfers.approve" },
      { title: "Customer Seed Approvals", url: "/production/customer-seed-approvals", permission: "seed_batches.approve_quality" },
    ],
  },
  {
    title: "Accounting",
    url: "#",
    icon: Calculator,
    items: [
      { title: "Chart of Accounts", url: "/accounting/chart-of-accounts", permission: "accounts.view" },
      { title: "Journal Entries", url: "/accounting/journal-entries", permission: "journal_entries.view" },
      { title: "Financial Reports", url: "/accounting/reports", permission: "financial_statements.view_trial_balance" },
      { title: "Bank Accounts", url: "/accounting/bank-accounts", permission: "bank_accounts.view" },
      { title: "Payment Methods", url: "/payment-methods", permission: "payment_methods.view" },
      { title: "Budgets", url: "/accounting/budgets", permission: "budgets.view" },
      { title: "Cost Centres", url: "/accounting/cost-centres", permission: "cost_centres.view" },
      { title: "Financial Years", url: "/accounting/financial-years", permission: "financial_years.view" },
    ],
  },
  {
    title: "Dispatch",
    url: "/dispatch",
    icon: Truck,
    items: [
      { title: "Dispatch Board", url: "/dispatch", permission: "dispatch.view" },
      { title: "Transport & Delivery Planner", url: "/dispatch/plans", permission: "dispatch.plans.view" },
      { title: "Tray Pickup Control", url: "/dispatch/tray-pickups", permission: "dispatch.view_outstanding_trays" },
      { title: "Delivery Reconciliation", url: "/dispatch/reconciliation", permission: "dispatch.reconcile_pending_delivery" },
      { title: "Trips", url: "/dispatch/trips", permission: "dispatch.view" },
      { title: "Compensation Notes", url: "/dispatch/compensation-notes", permission: "dispatch.approve_compensation" },
    ],
  },
  {
    title: "Agroforestry",
    url: "#",
    icon: Trees,
    items: [
      { title: "Receive Stock", url: "/agroforestry/receive", permission: "agroforestry.create_receipt" },
      { title: "Pending Approvals", url: "/agroforestry/approvals", permission: "agroforestry.approve_receipt" },
      { title: "Stock", url: "/agroforestry/stock", permission: "agroforestry.view_stock" },
      { title: "Discard Approvals", url: "/agroforestry/discard-approvals", permission: "discard_requests.approve" },
    ],
  },
  {
    title: "Reports",
    url: "#",
    icon: BookOpen,
    isActive: false,
    items: [
      { title: "Production", url: "#", kind: "label" },
      { title: "Sowing Breakdown", url: "/reports/production/sowing-breakdown", permission: "reports.view" },

      { title: "Financial", url: "#", kind: "label" },
      { title: "Financial Reports", url: "/reports", permission: "reports.view" },
      { title: "Directors Reports", url: "/directors-reports", permission: "directors_reports.view" },
    ],
  },
];

const navSetupsConfig: NavItem[] = [
  {
    title: "Crop / Seedlings Set Up",
    url: "#",
    icon: Sprout,
    items: [
      { title: "Crops & Varieties", url: "/production/crops", permission: "crop_types.view" },
      { title: "Variety Pricing", url: "/production/variety-pricing", permission: "pricings.view" },
      { title: "Setup Approvals", url: "/production/setup-approvals", permission: "crop_types.approve" },
      { title: "My Submissions", url: "/production/my-submissions", permission: "crop_types.submit_for_approval" },
    ],
  },
  {
    title: "Customer Set Ups",
    url: "#",
    icon: Users,
    items: [
      { title: "Customer Groups", url: "/customer-groups", permission: "customer_groups.view" },
    ],
  },
  {
    title: "Stocks Set Ups",
    url: "#",
    icon: Boxes,
    items: [
      { title: "Input Types", url: "/inventory/input-types", permission: "inventory.view" },
      { title: "Inputs", url: "/inventory/inputs", permission: "inventory.view" },
    ],
  },
  {
    title: "Greenhouses Set Ups",
    url: "#",
    icon: Warehouse,
    items: [
      { title: "Greenhouses", url: "/farms/greenhouses", permission: "farms.view" },
      { title: "Germination Chambers", url: "/production/germination-chambers", permission: "germination_chambers.view" },
    ],
  },
  {
    title: "User Set Ups",
    url: "#",
    icon: UserCog,
    items: [
      { title: "Users", url: "/admin/users", permission: "users.view" },
      { title: "Role Management", url: "/admin/roles", permission: "roles.view" },
      { title: "Audit Log", url: "/admin/audit-logs", permission: "audit_logs.view" },
    ],
  },
  {
    title: "Dispatch Set Ups",
    url: "#",
    icon: Truck,
    items: [
      { title: "Drivers", url: "/admin/drivers", permission: "drivers.view" },
      { title: "Vehicles", url: "/admin/vehicles", permission: "dispatch.route_configure" },
      { title: "Transport Rates", url: "/admin/transport-rates", permission: "transport_rates.view" },
      { title: "Route Templates", url: "/dispatch/route-templates", permission: "dispatch.route_configure" },
    ],
  },
  {
    title: "Loyalty Points",
    url: "#",
    icon: Settings,
    items: [
      { title: "Points Configuration", url: "/admin/loyalty-config", permission: "loyalty.config_view" },
      { title: "Config Approvals", url: "/admin/loyalty-config/approvals", permission: "loyalty.config_approve" },
    ],
  },
  {
    title: "Settings",
    url: "#",
    icon: Settings,
    items: [
      { title: "Company Profile", url: "/admin/company-profile", permission: "companies.view" },
      { title: "System Settings", url: "/admin/system-settings", permission: "system_settings.view" },
      { title: "Notification Center", url: "/admin/notification-center", permission: "notification_templates.view" },
    ],
  },
];

function filterNavItems(items: NavItem[], hasPermission: (p: string) => boolean): NavItem[] {
  return items
    .map((item) => {
      if (item.items) {
        const visibleChildren = item.items.filter(
          (child) => child.kind === "label" || !child.permission || hasPermission(child.permission)
        );
        const cleanedChildren = visibleChildren.filter((child, index, arr) => {
          if (child.kind !== "label") {
            return true;
          }
          const hasActionAfterLabel = arr
            .slice(index + 1)
            .some((next) => next.kind !== "label");
          return hasActionAfterLabel;
        });

        if (cleanedChildren.length === 0) return null;
        return { ...item, items: cleanedChildren };
      }
      if (item.permission && !hasPermission(item.permission)) return null;
      return item;
    })
    .filter((item): item is NavItem => item !== null);
}

export function AppSidebar({ ...props }: ComponentProps<typeof Sidebar>) {
  const { user, hasPermission } = useAuthStore();

  const visibleNavMain = filterNavItems(navMainConfig, hasPermission);
  const visibleNavSetups = filterNavItems(navSetupsConfig, hasPermission);

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <div className="flex items-center gap-2 px-2 py-1.5">
          <div className="bg-sidebar-primary text-sidebar-primary-foreground flex size-8 items-center justify-center rounded-lg">
            <GalleryVerticalEnd className="size-4" />
          </div>
          <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
            <span className="truncate font-semibold">FarmSmart ERP</span>
          </div>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <NavMain items={visibleNavMain} />
        {visibleNavSetups.length > 0 && (
          <NavMain items={visibleNavSetups} label="Administration" />
        )}
      </SidebarContent>
      <SidebarFooter>{user && <NavUser user={user} />}</SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
